import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useActiveAppStore } from "../../../store/activeAppStore";
import { useUserStore } from "../../../store/userStore";
import { OPEN_APPS_EVENT } from "../AppsRail/favoriteApps";
import type { Company } from "../../../store/admin/companiesStore";
import CompanySelector from "../CompanySelector";
import { LogoImage } from "../LogoImage";
import { AppTitle } from "../AppTitle";
import { useUserCompanyName } from "../../../hooks/useUserCompanyName";
import UnitNotificationsBell from "./UnitNotificationsBell";
import { pwa } from "../../../config/theme";
import { getWordmark } from "../../../config/brandAssets";

export interface SearchBarProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchClear?: () => void;
  searchPlaceholder?: string;
  searchResultCount?: number;
  searchTotalCount?: number;
}

export interface HeaderProps {
  title?: string;
  subtitle?: string;
  onOpenAppsSidebar?: () => void;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  isDarkMode?: boolean;
  isMobile?: boolean;
  isReseller?: boolean;
  companies?: Company[];
  selectedCompany?: string | null;
  onCompanyChange?: (company: string | null) => void;
  customActions?: ReactNode;
  showLogo?: boolean;
  showSearchBar?: boolean;
  searchBarProps?: SearchBarProps;
  hideProfileButton?: boolean;
  hideCompanySelector?: boolean;
}

export default function Header({
  title,
  subtitle,
  onOpenAppsSidebar,
  onToggleMobileMenu,
  isMobileMenuOpen,
  isDarkMode = false,
  isMobile = false,
  isReseller = false,
  companies = [],
  selectedCompany,
  onCompanyChange,
  customActions,
  showLogo = true,
  showSearchBar = false,
  searchBarProps,
  hideProfileButton = false,
  hideCompanySelector = false,
}: HeaderProps) {
  const { t } = useTranslation("common");
  const userRole = localStorage.getItem("userRole") || t("general.user");
  const userCompanyName = useUserCompanyName();

  const user = useUserStore((state) => state.user);

  // Get active app from store for dynamic title/subtitle and icon
  const activeAppTitle = useActiveAppStore((state) => state.title);
  const activeAppSubtitle = useActiveAppStore((state) => state.subtitle);
  const activeAppIcon = useActiveAppStore((state) => state.icon);
  const wordmark = getWordmark(isDarkMode);
  const activeAppIconColor = useActiveAppStore((state) => state.iconColor);

  // Use props if provided, otherwise fall back to active app store values
  const displayTitle = title || activeAppTitle || t("header.appTitle");
  const displaySubtitle =
    subtitle || activeAppSubtitle || t("header.dashboard");

  const themeClasses = {
    // Tema claro: blanco roto con un tinte leve del verde de la marca, en vez
    // del gris plano #e0e0e0, que se veía sucio junto al contenido blanco y no
    // compartía tono con los grises del resto. Mismo valor en la barra de
    // accesos y en los encabezados de admin, patio e inspección.
    bg: isDarkMode ? "bg-dark-bg" : "bg-[#f5f6f2]",
    border: isDarkMode ? "border-dark-border" : "border-[#e3e6de]",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-500",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-500",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
    inputBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    inputText: isDarkMode ? "text-white" : "text-gray-900",
    inputPlaceholder: isDarkMode
      ? "placeholder-gray-500"
      : "placeholder-gray-500",
  };

  // Botones de ícono del encabezado (campana, apps): 32 px con borde fino, como
  // los de ElevenLabs; antes eran íconos sueltos de 40 px con fondo al pasar.
  const iconButton = `relative size-8 grid place-items-center rounded-[10px] border transition-[background-color,transform] duration-150 ease-out active:scale-[0.96] ${
    isDarkMode
      ? "border-white/10 text-gray-300 hover:bg-white/[0.06] hover:text-white"
      : "border-black/[0.08] text-gray-600 hover:bg-black/[0.03] hover:text-gray-900"
  }`;

  return (
    <header
      className={`${themeClasses.bg} border-b ${themeClasses.border} ${isMobile ? "px-2 py-2" : "px-4 py-2.5"} shadow-sm`}
    >
      <div className="flex items-center justify-between gap-2">
        {/* Logo and Title */}
        <div className="flex items-center space-x-2 min-w-0">
          {/* Mobile Menu Toggle */}
          {onToggleMobileMenu && (
            <button
              onClick={onToggleMobileMenu}
              className={`lg:hidden p-2 ${themeClasses.buttonHover} ${themeClasses.buttonText} rounded-lg transition-all`}
              aria-label="Toggle menu"
            >
              <i
                className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}
              ></i>
            </button>
          )}
          {showLogo && wordmark ? (
            // Marca primero, app después. Wialon y Mapon abren con su logotipo y
            // v5 abría con "Mapa de Unidades" en un cuadro genérico: la marca no
            // aparecía en ninguna pantalla. El nombre de la app se queda, más
            // chico, detrás de un separador; el subtítulo pasa a tooltip. En
            // móvil sólo cabe el logotipo.
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={wordmark}
                alt={pwa.shortName}
                draggable={false}
                className={`${isMobile ? "h-6" : "h-8"} w-auto shrink-0 select-none`}
              />
              {!isMobile && (
                <>
                  <span
                    aria-hidden
                    className={`h-7 w-px shrink-0 ${isDarkMode ? "bg-white/15" : "bg-gray-300"}`}
                  />
                  <h1
                    title={displaySubtitle}
                    className={`text-sm font-semibold leading-tight truncate ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                  >
                    {displayTitle}
                  </h1>
                </>
              )}
            </div>
          ) : (
            <>
              {showLogo && (
                <LogoImage
                  size={isMobile ? "sm" : "md"}
                  appIcon={activeAppIcon}
                  appIconColor={activeAppIconColor}
                />
              )}
              <AppTitle
                title={displayTitle}
                subtitle={displaySubtitle}
                isDarkMode={isDarkMode}
                hideOnMobile={false}
              />
            </>
          )}

          {/* Company Selector (solo para reseller) */}
          {isReseller && !isMobile && !hideCompanySelector && (
            <CompanySelector
              value={selectedCompany ?? null}
              onChange={(id) => onCompanyChange?.(id)}
              isDarkMode={isDarkMode}
              isClearable
              companies={companies.length > 0 ? companies : undefined}
              compact
              className="w-48 sm:w-60 hidden md:block"
            />
          )}
        </div>

        {/* Search Bar */}
        {showSearchBar && !isMobile && searchBarProps && (
          <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <i
                className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.buttonText} text-sm`}
              ></i>
              <input
                type="text"
                value={searchBarProps.searchQuery || ""}
                onChange={(e) =>
                  searchBarProps.onSearchChange?.(e.target.value)
                }
                placeholder={
                  searchBarProps.searchPlaceholder ||
                  t("header.searchPlaceholder")
                }
                className={`w-full pl-10 pr-16 py-2 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.inputText} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:border-transparent transition-all text-sm`}
              />
              {searchBarProps.searchQuery && (
                <button
                  aria-label={t("general.close", { ns: "common" })}
                  onClick={searchBarProps.onSearchClear}
                  className={`absolute right-12 top-1/2 transform -translate-y-1/2 ${themeClasses.buttonText} hover:text-red-500`}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
              {searchBarProps.searchResultCount !== undefined && (
                <span
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs ${themeClasses.textSecondary}`}
                >
                  {searchBarProps.searchResultCount}/
                  {searchBarProps.searchTotalCount}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div
          className={`flex items-center flex-shrink-0 ${isMobile ? "space-x-1" : "space-x-2"}`}
        >
          {/* Campana de notificaciones (unidades y plataforma) en todas las
              apps: salió de la appsuite (Daniel, 26-oct). */}
          <UnitNotificationsBell
            isDarkMode={isDarkMode}
            buttonClass={iconButton}
          />

          {/* Custom Actions Slot */}
          {customActions}

          {/* Todas las apps, junto al avatar (Daniel, 30-oct: muchos usuarios lo
              buscan ahí). Círculo con la cuadrícula de 3×3 puntos, como el de
              Facebook; antes vivía al pie de la barra de accesos y en el
              encabezado sólo salía en móvil. */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(OPEN_APPS_EVENT))}
            aria-label={t("appsRail.all", "Todas las apps")}
            title={t("appsRail.all", "Todas las apps")}
            className={`size-8 shrink-0 grid place-items-center rounded-full transition-[background-color,transform] duration-150 ease-out active:scale-[0.96] ${
              isDarkMode ? "bg-white/[0.08] text-gray-200 hover:bg-white/[0.14]" : "bg-black/[0.05] text-gray-700 hover:bg-black/[0.09]"
            }`}
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="size-[15px]" aria-hidden>
              {[2.5, 8, 13.5].flatMap((y) => [2.5, 8, 13.5].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" />))}
            </svg>
          </button>

          {/* Perfil: sólo el círculo (Daniel, 30-oct, referencia ElevenLabs). El
              nombre, la compañía y el rol ocupaban ~220 px del encabezado y ya
              están en el menú; el cuadro verde era el elemento más llamativo de
              la pantalla sin decir nada. Iniciales sobre gris neutro hasta que
              el usuario tenga foto. */}
          {!hideProfileButton && (
            <button
              type="button"
              onClick={onOpenAppsSidebar}
              aria-label={t("appsSidebar.profileMenu", "Menú de perfil")}
              title={
                user?.first_name && user?.last_name
                  ? `${user.first_name} ${user.last_name}${userCompanyName ? ` · ${userCompanyName}` : ""}`
                  : user?.email || userRole
              }
              className={`size-8 shrink-0 rounded-full grid place-items-center text-[11px] font-semibold tracking-wide transition-[transform,box-shadow] duration-150 ease-out active:scale-[0.96] ring-1 hover:ring-2 ${
                isDarkMode
                  ? "bg-white/10 text-gray-100 ring-white/15 hover:ring-white/25"
                  : "bg-gray-200/70 text-gray-700 ring-black/10 hover:ring-black/20"
              }`}
            >
              {user?.first_name && user?.last_name
                ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
                : (user?.email?.charAt(0) || "U").toUpperCase()}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
